import { motion } from "framer-motion";
import { ShieldCheck, User, Zap, Star, Building2, Sparkles, ArrowUpRight } from "lucide-react";
import { BRAND } from "../lib/api";

const PILLARS = [
  {
    icon: User,
    tag: "02",
    title: "Owner-Operated",
    body:
      "The person who quotes your job is on your job. No middlemen, no phone tag, no surprises.",
  },
  {
    icon: Zap,
    tag: "03",
    title: "Same-Day Free Quotes",
    body:
      "Call, text, or fill the form — we get back to you today with a real, honest number.",
  },
  {
    icon: Star,
    tag: "04",
    title: "5.0 Stars · 17+ Google Reviews",
    body:
      "Every single Google review from real customers across Central Alabama is 5 stars. Not a coincidence.",
  },
  {
    icon: Building2,
    tag: "05",
    title: "Residential & Commercial",
    body:
      "From single rooms to storefronts, showrooms, and offices — we scale to the job.",
  },
];

export default function TrustUs() {
  return (
    <section
      id="trust"
      data-testid="trust-section"
      className="relative py-24 md:py-40 border-t border-brand-border overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand-muted mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-brand-blue" />
              05 — Why RRR
            </div>
            <h2 className="font-display uppercase text-6xl md:text-8xl leading-[0.88]">
              Backed By <br />
              <span className="text-brand-blue">Our Word.</span>
            </h2>
          </div>
          <p className="text-brand-muted max-w-sm text-base">
            We don&rsquo;t just promise quality — we guarantee it. Here&rsquo;s
            what you get when you hire RRR.
          </p>
        </div>

        {/* FEATURED — 100% Satisfaction Guarantee */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.6, 0.05, 0.15, 1] }}
          data-testid="satisfaction-guarantee"
          className="relative bg-brand-blue text-white p-8 md:p-14 mb-4 overflow-hidden group cursor-default"
        >
          {/* Giant background number */}
          <div className="absolute -top-6 -right-4 md:-right-10 font-display text-white/10 text-[28vw] md:text-[18vw] leading-none pointer-events-none select-none">
            100%
          </div>

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-1">
              <div className="inline-flex items-center justify-center h-16 w-16 md:h-20 md:w-20 border-2 border-white/40 rounded-full">
                <ShieldCheck className="h-8 w-8 md:h-10 md:w-10" strokeWidth={2} />
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/70 mb-3 flex items-center gap-3">
                <span className="h-px w-8 bg-white/70" />
                01 — The Promise
              </div>
              <h3 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight">
                100% Satisfaction, <br className="hidden md:block" />
                <span className="italic font-serif normal-case tracking-normal">
                  guaranteed.
                </span>
              </h3>
              <p className="mt-6 text-white/90 text-base md:text-lg max-w-xl leading-relaxed">
                If you&rsquo;re not satisfied when we&rsquo;re done, we&rsquo;re
                not done. We do a walk-through with you before the truck
                leaves — and we don&rsquo;t leave until it&rsquo;s right.
              </p>
            </div>

            <div className="lg:col-span-4 flex flex-col gap-3">
              <a
                href={BRAND.phoneHref}
                data-testid="trust-call-btn"
                className="group/btn flex items-center justify-between gap-3 bg-white text-brand-blue hover:bg-brand-bg hover:text-white px-6 py-4 font-display tracking-wider uppercase text-lg transition-colors"
              >
                Call {BRAND.phone}
                <ArrowUpRight className="h-5 w-5 group-hover/btn:rotate-45 transition-transform" />
              </a>
              <button
                onClick={() =>
                  document
                    .getElementById("lead-form")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                data-testid="trust-quote-btn"
                className="group/btn flex items-center justify-between gap-3 border border-white/40 hover:border-white text-white px-6 py-4 font-display tracking-wider uppercase text-lg transition-colors"
              >
                Get Free Quote
                <ArrowUpRight className="h-5 w-5 group-hover/btn:rotate-45 transition-transform" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Trust pillars grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-brand-border">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              data-testid={`trust-pillar-${i}`}
              className={`group relative p-8 flex flex-col gap-4 min-h-[280px] bg-brand-bg hover:bg-brand-surface transition-colors ${
                i < PILLARS.length - 1 ? "border-b md:border-b-0 md:border-r border-brand-border" : ""
              } ${i === 1 ? "md:border-b lg:border-b-0" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-brand-muted text-xl tracking-widest">
                  {p.tag}
                </span>
                <p.icon className="h-6 w-6 text-brand-blue" strokeWidth={1.5} />
              </div>
              <h4 className="font-display uppercase text-2xl md:text-3xl leading-[0.95] mt-2">
                {p.title}
              </h4>
              <p className="text-brand-muted text-sm md:text-base leading-relaxed">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Footer badge */}
        <div className="mt-10 flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-brand-muted">
          <Sparkles className="h-4 w-4 text-brand-blue" />
          Great Quality. Great Experience. Every job. Every wall.
        </div>
      </div>
    </section>
  );
}
